<script setup lang="ts">
import { type PropType, ref, watch } from "vue";
import StatusIcon from "../participants/StatusIcon.vue";
import InputModeTag from "./InputModeTag.vue";
import MoveParticipant from "./MoveParticipant.vue";
import ParticipantActivityTimeline from "./ParticipantActivityTimeline.vue";
import EmailDeliveryStatus from "../EmailDeliveryStatus.vue";
import ResendEmail from "./ResendEmail.vue";
import ResetQuestionnaire from "./ResetQuestionnaire.vue";
import RegenerateParticipant from "./RegenerateParticipant.vue";
import SilentRegenerateParticipants from "./SilentRegenerateParticipants.vue";
import DeleteParticipant from "./DeleteParticipant.vue";
import { ProfileAdministrationAPI } from "../../api";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { EmailReferenceType, Participant, Project, Trainer } from "../../types/PersologOnlineTypes.ts";
import { questionnaireUrl } from "../../utils/supportLinks.ts";
import type { DataTableRowExpandEvent } from "primevue/datatable";

const props = defineProps({
    participants: Object as PropType<Participant[] | null>,
});

// A move changes the participant's project, so the search result has to be fetched again.
const emit = defineEmits<{ reload: [] }>();

const { t } = useI18n();
const toast = useToast();

type RowDetails = { project?: Project; trainer?: Trainer };

const expandedRows = ref<Participant[]>([]);
// Selected for a bulk action (RDT-497). A new search result drops what is no longer in it.
const selectedParticipants = ref<Participant[]>([]);
watch(
    () => props.participants,
    (participants) => {
        selectedParticipants.value = selectedParticipants.value.filter((selected) => participants?.some((p) => p.id === selected.id));
    },
);
// Kept per participant: a single shared ref showed the trainer of whichever row was expanded last
// in every other open row.
const rowDetails = ref<Record<number, RowDetails>>({});

async function loadRowDetails(participant: Participant) {
    const participantId = participant.id;
    if (!participantId || rowDetails.value[participantId]) {
        return;
    }

    try {
        const project = await ProfileAdministrationAPI.fetchProject(participant.projectId);
        const trainer = project.trainer ? await ProfileAdministrationAPI.fetchTrainer(project.trainer) : undefined;
        rowDetails.value[participantId] = { project, trainer };
    } catch (error) {
        console.error("Could not load the project and trainer of the participant", error);
        toast.add({
            severity: "error",
            summary: t("messages.error"),
            detail: t("messages.load.error"),
            life: 4000,
        });
    }
}

const onRowExpand = (event: DataTableRowExpandEvent) => loadRowDetails(event.data as Participant);
</script>

<template>
    <div class="row">
        <div class="flex-1">
            <SilentRegenerateParticipants
                v-if="participants?.length"
                :participants="selectedParticipants"
                class="pt-8"
                @done="selectedParticipants = []"
            />
            <div class="d-flex pt-8">
                <!-- Main table -->
                <DataTable
                    v-if="participants?.length !== 0"
                    v-model:expanded-rows="expandedRows"
                    v-model:selection="selectedParticipants"
                    :value="participants"
                    paginator
                    :rows="10"
                    :rows-per-page-options="[5, 10, 20, 50]"
                    data-key="id"
                    @rowExpand="onRowExpand"
                >
                    <Column selection-mode="multiple" header-style="width: 3rem" />
                    <Column expander style="width: 5rem" />
                    <Column field="id" :header="t('pending.participants.id')" :sortable="true"></Column>
                    <Column field="name" :header="t('pending.participants.lastname')">
                        <template #body="slotProps">
                            {{ slotProps.data.firstName.concat(" " + slotProps.data.name) }}
                        </template>
                    </Column>
                    <Column field="email" :header="t('pending.participants.email')"></Column>
                    <Column field="status" :header="t('pending.participants.status')">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2">
                                <StatusIcon :status="slotProps.data.status"></StatusIcon>
                                <InputModeTag :input-mode="slotProps.data.inputMode" />
                            </div>
                        </template>
                    </Column>
                    <Column :header="t('pending.participants.actions')">
                        <template #body="slotProps">
                            <div class="flex items-center">
                                <RegenerateParticipant :participant="slotProps.data" />
                                <MoveParticipant :participant="slotProps.data" @moved="emit('reload')" />
                                <ResetQuestionnaire :participant="slotProps.data" @reset="emit('reload')" />
                                <ResendEmail :participant="slotProps.data" @resent="emit('reload')" />
                                <DeleteParticipant :participant="slotProps.data" @deleted="emit('reload')" />
                            </div>
                        </template>
                    </Column>
                    <template #expansion="slotProps">
                        <div :data-cy="`participant-details-${slotProps.data.id}`">
                            <div>{{ t("pending.participants.id") }}: {{ slotProps.data.id }}</div>
                            <div>
                                {{ t("projects.label") }} ({{ t("projects.id") }}):
                                {{ rowDetails[slotProps.data.id]?.project?.name }} ({{ slotProps.data.projectId }})
                            </div>
                            <div>
                                {{ t("projects.trainer") }}:
                                {{ rowDetails[slotProps.data.id]?.trainer?.firstname }}
                                {{ rowDetails[slotProps.data.id]?.trainer?.lastname }}
                            </div>
                            <div class="flex gap-4">
                                <router-link
                                    v-if="rowDetails[slotProps.data.id]?.trainer"
                                    :to="{ path: '/projects', query: { id: rowDetails[slotProps.data.id]?.trainer?.id } }"
                                >
                                    {{ t("support.links.showProjects") }}
                                </router-link>
                                <router-link
                                    v-if="rowDetails[slotProps.data.id]?.trainer"
                                    :to="{ path: '/trainers', query: { email: rowDetails[slotProps.data.id]?.trainer?.email } }"
                                >
                                    {{ t("support.links.showTrainer") }}
                                </router-link>
                            </div>
                            <div>
                                {{ t("projects.questionnaire") }}:
                                <a
                                    v-if="questionnaireUrl(slotProps.data.questionnaire)"
                                    :href="questionnaireUrl(slotProps.data.questionnaire)"
                                    target="_blank"
                                >
                                    {{ slotProps.data.questionnaire }}
                                </a>
                                <span v-else>{{ slotProps.data.questionnaire }}</span>
                            </div>
                            <EmailDeliveryStatus
                                v-if="slotProps.data.email && slotProps.data.id"
                                :email="slotProps.data.email"
                                :reference-type="EmailReferenceType.PARTICIPANT"
                                :reference-id="String(slotProps.data.id)"
                                class="mt-4"
                            />
                            <ParticipantActivityTimeline
                                v-if="slotProps.data.id"
                                :participant-id="slotProps.data.id"
                                class="mt-4"
                            />
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
</template>
